import React, { Component } from 'react'

const STEP_COUNT = 32
/** Luminancias fijas DICOM-style (8-bit): 0% / 5% / 95% / 100% */
const GRAY_0 = 0
const GRAY_5 = 13
const GRAY_95 = 242
const GRAY_100 = 255
/** Fracción del lado del canvas para el contenedor exterior de ambos parches */
const PATCH_OUTER_FRAC = 0.2
/** Relación interior/exterior (mismo valor en D0/D5 y D95/D100) */
const PATCH_INNER_RATIO = 0.5

/**
 * Patrón TG18-QC simplificado (Canvas 2D, 8-bit sRGB, sin LUT ni filtros CSS).
 * 1 CSS px = 1 canvas px para rejillas de 1px nítidas.
 */
class GsdfQaView extends Component {
  constructor(props) {
    super(props)
    this.wrapRef = React.createRef()
    this.canvasRef = React.createRef()
    this._ro = null
  }

  componentDidMount() {
    this.paint()
    if (typeof ResizeObserver !== 'undefined' && this.wrapRef.current) {
      this._ro = new ResizeObserver(() => this.paint())
      this._ro.observe(this.wrapRef.current)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.paint)
    }
  }

  componentWillUnmount() {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.paint)
    }
  }

  getContext = (canvas) => {
    let ctx = null
    try {
      ctx = canvas.getContext('2d', { colorSpace: 'srgb', alpha: false })
    } catch (e) {
      ctx = null
    }
    if (!ctx) {
      try {
        ctx = canvas.getContext('2d', { alpha: false })
      } catch (e2) {
        ctx = canvas.getContext('2d')
      }
    }
    return ctx
  }

  gray = (n) => {
    const v = Math.max(0, Math.min(255, n | 0))
    return `rgb(${v},${v},${v})`
  }

  fillRectGray = (ctx, x, y, w, h, level) => {
    ctx.fillStyle = this.gray(level)
    ctx.fillRect(x | 0, y | 0, w | 0, h | 0)
  }

  drawChecker = (ctx, x, y, size) => {
    const s = Math.max(8, size | 0)
    for (let row = 0; row < s; row++) {
      for (let col = 0; col < s; col++) {
        const on = (row + col) % 2 === 0
        ctx.fillStyle = on ? this.gray(255) : this.gray(0)
        ctx.fillRect((x + col) | 0, (y + row) | 0, 1, 1)
      }
    }
  }

  paint = () => {
    const wrap = this.wrapRef.current
    const canvas = this.canvasRef.current
    if (!wrap || !canvas) return

    const availW = Math.max(1, Math.floor(wrap.clientWidth))
    const availH = Math.max(1, Math.floor(wrap.clientHeight))
    // Cuadrado 1:1 centrado (letterbox negro en el wrap)
    const side = Math.max(64, Math.min(availW, availH))

    if (canvas.width !== side || canvas.height !== side) {
      canvas.width = side
      canvas.height = side
    }

    const ctx = this.getContext(canvas)
    if (!ctx) return

    ctx.imageSmoothingEnabled = false
    this.fillRectGray(ctx, 0, 0, side, side, 0)

    const margin = Math.max(8, Math.floor(side * 0.04))
    const content = side - margin * 2

    // --- Rampa de grises (32 escalones) ---
    const rampH = Math.max(24, Math.floor(side * 0.12))
    const rampY = margin
    const stepW = content / STEP_COUNT
    for (let i = 0; i < STEP_COUNT; i++) {
      const level = Math.round((i / (STEP_COUNT - 1)) * 255)
      const x0 = Math.floor(margin + i * stepW)
      const x1 = Math.floor(margin + (i + 1) * stepW)
      this.fillRectGray(ctx, x0, rampY, Math.max(1, x1 - x0), rampH, level)
    }

    // --- Parches D0/D5 y D95/D100 (mismas dimensiones en ambos lados) ---
    const patchSize = Math.max(48, Math.floor(side * PATCH_OUTER_FRAC))
    const innerPatchSize = Math.max(1, Math.floor(patchSize * PATCH_INNER_RATIO))
    // Centrado idéntico: mismo offset en X e Y para ambos parches
    const innerOffset = Math.floor((patchSize - innerPatchSize) / 2)
    const patchY = rampY + rampH + Math.max(12, Math.floor(side * 0.06))

    const drawContrastPatch = (outerX, outerLevel, innerLevel) => {
      this.fillRectGray(ctx, outerX, patchY, patchSize, patchSize, outerLevel)
      this.fillRectGray(
        ctx,
        outerX + innerOffset,
        patchY + innerOffset,
        innerPatchSize,
        innerPatchSize,
        innerLevel
      )
    }

    // D0 fondo rgb(0,0,0) / D5 centro rgb(13,13,13)
    drawContrastPatch(margin, GRAY_0, GRAY_5)
    // D100 fondo rgb(255,255,255) / D95 centro rgb(242,242,242)
    drawContrastPatch(side - margin - patchSize, GRAY_100, GRAY_95)

    // --- Rejillas 1px (esquinas + centro) ---
    const grid = Math.max(12, Math.min(32, Math.floor(side * 0.06)))
    const bottom = side - margin - grid
    this.drawChecker(ctx, margin, bottom, grid)
    this.drawChecker(ctx, side - margin - grid, bottom, grid)
    this.drawChecker(
      ctx,
      Math.floor((side - grid) / 2),
      Math.floor((side - grid) / 2),
      grid
    )
  }

  render() {
    return (
      <div
        ref={this.wrapRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: 200,
          backgroundColor: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={this.canvasRef}
          aria-label="Patrón de prueba GSDF TG18-QC"
          style={{
            display: 'block',
            backgroundColor: '#000000',
            imageRendering: 'pixelated',
            // Safari / Firefox
            // @ts-ignore
            WebkitImageRendering: 'pixelated',
            msInterpolationMode: 'nearest-neighbor',
            maxWidth: '100%',
            maxHeight: '100%',
            filter: 'none',
          }}
        />
      </div>
    )
  }
}

export default GsdfQaView
